/**
 * FOS Decision Card
 *
 * Reusable molecule component for displaying ombudsman decision cards.
 * Supports multiple variants for different contexts:
 * - default: Full card for results list with all details
 * - compact: Smaller card for cluster panel and side views
 *
 * Uses GO brand colors via OutcomeBadge:
 * - Berry → Upheld (risk to firm)
 * - Teal → Not Upheld (favorable)
 * - Purple → Partially Upheld (needs review)
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../../lib/utils';
import { OutcomeBadge, normalizeOutcome } from '../../ui/outcome-badge';
import { RelevanceIndicator } from '../../ui/relevance-indicator';
import type { FOSDecisionCard as FOSDecisionCardData } from '../../../utils/api';

const fosDecisionCardVariants = cva(
  'group relative bg-card cursor-pointer transition-all duration-150',
  {
    variants: {
      variant: {
        default: 'rounded-lg border border-border/50 p-5 hover:border-border/70 hover:shadow-sm',
        list: 'rounded-none border-0 px-4 py-5 hover:bg-muted/30',
        compact: 'p-4 hover:border-border hover:shadow-sm',
      },
      showHoverIndicator: {
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      showHoverIndicator: true,
    },
  }
);

export interface FOSDecisionCardProps
  extends VariantProps<typeof fosDecisionCardVariants> {
  /** The decision data to display */
  decision: FOSDecisionCardData;
  /** Click handler */
  onClick?: () => void;
  /** Whether to show similarity score (for search results) */
  showSimilarityScore?: boolean;
  /** Whether to show company name */
  showCompany?: boolean;
  /** Whether to show the date */
  showDate?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Compensation Badge Component
 */
const CompensationBadge: React.FC<{ amount: number }> = ({ amount }) => (
  <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200/50">
    <span className="text-sm font-semibold text-emerald-700 tabular-nums">
      £{amount.toLocaleString()}
    </span>
  </div>
);

/**
 * Meta Info Row Component
 */
const MetaInfoRow: React.FC<{
  company?: string | null;
  productCategory?: string | null;
  decisionDate?: string | null;
  showCompany: boolean;
  showDate: boolean;
}> = ({ company, productCategory, decisionDate, showCompany, showDate }) => {
  const items: React.ReactNode[] = [];

  if (showCompany && company) {
    items.push(
      <span key="company">
        {company}
      </span>
    );
  }

  if (productCategory) {
    if (items.length > 0) {
      items.push(<span key="sep1" className="text-border">·</span>);
    }
    items.push(
      <span key="product">
        {productCategory}
      </span>
    );
  }

  if (showDate && decisionDate) {
    if (items.length > 0) {
      items.push(<span key="sep2" className="text-border">·</span>);
    }
    items.push(
      <span key="date" className="tabular-nums whitespace-nowrap">
        {new Date(decisionDate).toLocaleDateString('en-GB', {
          month: 'short',
          year: 'numeric',
        })}
      </span>
    );
  }

  if (items.length === 0) return null;

  return (
    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {items}
    </div>
  );
};

/**
 * FOS Decision Card Component
 */
export const FOSDecisionCard: React.FC<FOSDecisionCardProps> = ({
  decision,
  onClick,
  variant = 'default',
  showHoverIndicator = true,
  showSimilarityScore = true,
  showCompany = true,
  showDate = true,
  className,
}) => {
  const outcomeNormalized = normalizeOutcome(decision.outcome);
  const isCompact = variant === 'compact';
  const hasCompensation =
    decision.total_compensation !== null && decision.total_compensation > 0;

  return (
    <div
      className={cn(fosDecisionCardVariants({ variant, showHoverIndicator }), className)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left side - main info */}
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className={cn('flex items-center gap-3', isCompact ? 'mb-3' : 'mb-4')}>
            <span
              className={cn(
                'font-mono font-medium text-foreground',
                isCompact ? 'text-xs' : 'text-[13px]'
              )}
            >
              {decision.reference}
            </span>
            <OutcomeBadge
              outcome={outcomeNormalized}
              size="sm"
              showLabel={!isCompact}
            />
          </div>

          {/* Core issue */}
          {decision.core_issue && (
            <p
              className={cn(
                'text-foreground/80 leading-relaxed',
                isCompact ? 'text-xs line-clamp-2 mb-3' : 'text-[15px] line-clamp-2 mb-4'
              )}
            >
              {decision.core_issue}
            </p>
          )}

          {/* Meta row */}
          <MetaInfoRow
            company={decision.company}
            productCategory={decision.product_category}
            decisionDate={decision.decision_date}
            showCompany={showCompany}
            showDate={showDate}
          />

          {/* Compensation - inline for compact variant */}
          {isCompact && hasCompensation && (
            <div className="flex items-center gap-2 mt-1.5 text-xs">
              <span className="text-emerald-600 font-medium">
                £{decision.total_compensation!.toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* Right side - relevance indicator and/or compensation */}
        {(showSimilarityScore && decision.similarity_score !== null) || (!isCompact && hasCompensation) ? (
          <div className="flex-shrink-0 flex flex-col items-end gap-3">
            {showSimilarityScore && decision.similarity_score !== null && (
              <RelevanceIndicator
                value={decision.similarity_score}
                size="sm"
                showLabel={false}
                showTooltip={true}
              />
            )}
            {!isCompact && hasCompensation && (
              <CompensationBadge amount={decision.total_compensation!} />
            )}
          </div>
        ) : null}
      </div>

      {/* Hover indicator */}
      {showHoverIndicator && (
        <div className="absolute inset-y-0 left-0 w-0.5 bg-primary rounded-l-lg opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );
};

export default FOSDecisionCard;
